import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { TodoResponse } from "@/lib/api";
import { cn, formatDateOnly, getCardBackgroundColor, getTagColor, toTitleCase } from "@/lib/utils";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDown, ArrowUp, Calendar, Check, CheckCircle, ChevronLeft, ChevronRight, Circle, Clock, Edit3, ExternalLink, Trash2, X
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

export interface TodoCardProps {
  todo: TodoResponse;
  job?: { id: string; title: string; company: string };
  sectionHeader?: string;
  categoryCounts: Record<string, any[]>;
  showActions: boolean;
  onToggleActions: (todoId: string) => void;
  onMoveUp: (todoId: string) => void;
  onMoveDown: (todoId: string) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onEdit: (todo: TodoResponse) => void;
  onDelete: (todoId: string) => void;
  onToggleComplete: (todoId: string, completed: boolean) => void;
  completionState: 'idle' | 'confirming';
  onCompletionClick: (todoId: string) => void;
  onConfirmCompletion: (todoId: string) => void;
  onCancelCompletion: (todoId: string) => void;
  isDragging?: boolean;
  index?: number;
  activeId?: string;
}

export const DraggableTodoCard: React.FC<TodoCardProps> = ({
  todo,
  job,
  sectionHeader,
  categoryCounts,
  showActions,
  onToggleActions,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onEdit,
  onDelete,
  onToggleComplete,
  completionState,
  onCompletionClick,
  onConfirmCompletion,
  onCancelCompletion,
  isDragging,
  index,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isCurrentlyDragging } = useSortable({ id: todo.id });
  const style: React.CSSProperties = {
    transform: isCurrentlyDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={{ ...style, opacity: isCurrentlyDragging ? 0 : 1 }}
      {...attributes}
      {...listeners}
      className={cn("relative mb-4 w-full rounded-lg shadow-sm overflow-hidden", isDragging && "opacity-50")}
    >      
        {/* Action Panel (positioned absolutely, revealed by card's transform) */}
        <div
          className="absolute top-0 right-0 h-full w-48 bg-white border-l border-gray-200"
        >
          <div className="h-full flex flex-col justify-center p-3 gap-2">
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" className={cn("h-8", !canMoveUp && "opacity-40")} disabled={!canMoveUp} onClick={(e) => { e.stopPropagation(); onMoveUp(todo.id); }} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" className="h-8" onClick={(e) => { e.stopPropagation(); onEdit(todo); }} aria-label="Edit"><Edit3 className="h-4 w-4" /></Button>
              <Button variant="outline" size="sm" className={cn("h-8", !canMoveDown && "opacity-40")} disabled={!canMoveDown} onClick={(e) => { e.stopPropagation(); onMoveDown(todo.id); }} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
              <Button variant="destructive" size="sm" className="h-8" onClick={(e) => { e.stopPropagation(); onDelete(todo.id); }} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
            </div>
            
            {todo.completed ? (
              <Button variant="outline" size="sm" className="h-10 w-full gap-2 bg-green-50 border-green-300 text-green-600 hover:bg-green-100" onClick={(e) => { e.stopPropagation(); onToggleComplete(todo.id, false); }} aria-label="Mark as incomplete"><CheckCircle className="h-4 w-4" />Mark Incomplete</Button>
            ) : (
              completionState === 'idle' ? (
                <Button variant="outline" size="sm" className="h-10 w-full gap-2 border-green-300 text-green-600 hover:bg-green-50" onClick={(e) => { e.stopPropagation(); onCompletionClick(todo.id); }} aria-label="Mark as complete"><Circle className="h-4 w-4" />Mark Done</Button>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Mark done?</span>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:bg-green-100" onClick={(e) => { e.stopPropagation(); onConfirmCompletion(todo.id); }}><Check className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-100" onClick={(e) => { e.stopPropagation(); onCancelCompletion(todo.id); }}><X className="h-4 w-4" /></Button>
                </div>
              )
            )}
          </div>
        </div>

        {/* Main Card Content (slides to reveal actions) */}
        <div 
          className={cn(
            "relative z-10 w-full transition-transform duration-300 ease-in-out bg-white",
            showActions ? "-translate-x-48" : "translate-x-0"
          )}
          {...attributes} {...listeners} /* Drag handles on the whole card */
        >
          <Card 
            className={cn(
              "transition-all duration-200 hover:shadow-lg rounded-lg min-h-28 relative",
              getCardBackgroundColor(todo),
              isDragging && "rotate-1 shadow-xl scale-105"
            )}
          >
            <div className="flex">
              <div className="flex-1">
                <CardContent className="p-4 pr-14">
                    <div className="flex flex-wrap items-start gap-2 mb-2">
                      <h3 className={cn(
                        "font-semibold text-base leading-tight flex-shrink-0",
                        todo.completed && "line-through text-gray-500"
                      )}>
                        {todo.title}
                      </h3>
                      
                      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        {todo.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDateOnly(todo.due_date)}
                          </span>
                        )}
                        
                        {job && (
                          <Link 
                            to={`/jobs/${job.id}`}
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 pointer-events-auto"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                            {job.company}
                          </Link>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mb-2">
                      <Badge
                        className={cn(
                          "text-xs px-2 py-0.5 text-white font-medium",
                          todo.priority === 'high' && "bg-red-600",
                          todo.priority === 'medium' && "bg-yellow-600",
                          todo.priority === 'low' && "bg-green-600"
                        )}
                      >
                        {toTitleCase(todo.priority)}
                      </Badge>
                      
                      {todo.remind_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDateOnly(todo.remind_at)}
                        </span>
                      )}
                    </div>
                    
                    {todo.description && (
                      <p className={cn(
                        "text-sm text-gray-700 mb-2 leading-relaxed",
                        todo.completed && "text-gray-400"
                      )}>
                        {todo.description}
                      </p>
                    )}
                    
                    {todo.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {todo.tags.map(tag => (
                          <Badge 
                            key={tag}
                            className="text-xs px-2 py-0.5 text-white font-medium"
                            style={{ 
                              backgroundColor: getTagColor(tag)
                            }}
                          >
                            {tag.split('-').map(word => toTitleCase(word)).join(' ')}
                          </Badge>
                        ))}
                      </div>
                    )}
                </CardContent>
              </div>
              
            </div>
          </Card>

          {/* Toggle Button (absolute full-height within sliding container) */}
          <div 
            className="absolute inset-y-0 right-0 w-12 flex items-center justify-center bg-blue-500 hover:bg-blue-600 transition-colors duration-300 cursor-pointer rounded-r-lg"
            onClick={(e) => { e.stopPropagation(); onToggleActions(todo.id); }}
          >
            <Button variant="ghost" size="sm" className="h-full w-full rounded-none text-white hover:text-white hover:bg-transparent">
              {showActions ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </div>
  );
};

// Static TodoCard for drag overlay
export const TodoCard: React.FC<Pick<TodoCardProps, 'todo' | 'job' | 'isDragging'>> = ({ todo, job, isDragging }) => {
  return (
    <Card className={cn(
      "transition-shadow hover:shadow-lg rounded-lg min-h-28",
      getCardBackgroundColor(todo),
      isDragging && "opacity-50 rotate-1 shadow-xl scale-105"
    )}>
      <CardContent className="p-4">
        <div className="flex flex-wrap items-start gap-2 mb-2">
          <h3 className={cn(
            "font-semibold text-base leading-tight flex-shrink-0",
            todo.completed && "line-through text-gray-500"
          )}>
            {todo.title}
          </h3>
          
          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
            {todo.due_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDateOnly(todo.due_date)}
              </span>
            )}
            {job && (
              <span className="flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                {job.company}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600 mb-2">
          <Badge
            className={cn(
              "text-xs px-2 py-0.5 text-white font-medium",
              todo.priority === 'high' && "bg-red-600",
              todo.priority === 'medium' && "bg-yellow-600",
              todo.priority === 'low' && "bg-green-600"
            )}
          >
            {toTitleCase(todo.priority)}
          </Badge>
          {todo.remind_at && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDateOnly(todo.remind_at)}
            </span>
          )}
        </div>

        {todo.description && (
          <p className={cn(
            "text-sm text-gray-700 mb-2 leading-relaxed",
            todo.completed && "text-gray-400"
          )}>
            {todo.description}
          </p>
        )}

        {todo.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {todo.tags.map(tag => (
              <Badge 
                key={tag}
                className="text-xs px-2 py-0.5 text-white font-medium"
                style={{ 
                  backgroundColor: getTagColor(tag)
                }}
              >
                {toTitleCase(tag)}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
